// src/features/mocktests/components/TestResultsPopup.js
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";

const TestResultsPopup = ({
  open,
  onClose,
  resultsData,
  onContinue,
  attemptId,
  mockTestId,
  baseRoute = "/mock-test",
  backRoute = "/app/mocktests",
}) => {
  const navigate = useNavigate();

  // Effect to pause timers and audio when popup opens
  useEffect(() => {
    if (open) {
      // Pause all audio elements
      const audioElements = document.querySelectorAll("audio");
      audioElements.forEach((audio) => {
        if (!audio.paused) {
          audio.pause();
        }
      });

      // Stop/pause timers by dispatching a custom event
      window.dispatchEvent(new CustomEvent("pauseTestTimers"));
    }
  }, [open]);

  const handleContinue = () => {
    // Call the onContinue callback if provided
    if (onContinue) {
      onContinue();
    } else {
      // Navigate to back route (defaults to mock tests page)
      navigate(backRoute);
    }

    // Close the popup
    onClose();
  };

  // Handle viewing results - navigate to attempt result page
  const handleViewResult = () => {
    // Prefer explicit mockTestId and attemptId props; fall back to values in resultsData if available
    const attempt =
      attemptId ||
      resultsData?.data?.studentAttemptId ||
      resultsData?.data?._id;
    const mockId =
      mockTestId || resultsData?.data?.mockTestId || resultsData?.data?.testId;

    if (attempt && mockId) {
      // Close the popup first
      if (onClose) onClose();
      
      // Determine the correct result route based on baseRoute
      let resultRoute;
      if (baseRoute.includes('bundles')) {
        // Bundle route format
        resultRoute = `${baseRoute}/attempt/${attempt}/result`;
      } else if (baseRoute.includes('mini-mock-test')) {
        // Mini mock test route format
        resultRoute = `/app/mini-mock-tests/${mockId}/attempt/${attempt}/result`;
      } else if (baseRoute.includes('class-test')) {
        // Class test route format
        resultRoute = `/app/class-tests/${mockId}/attempt/${attempt}/result`;
      } else {
        // Full mock test route format
        resultRoute = `/app/mock-tests/${mockId}/attempt/${attempt}/result`;
      }
      
      // Navigate to the attempt result page
      navigate(resultRoute);
    } else {
      // If we don't have ids, just close and fallback to continue behavior
      if (onContinue) onContinue();
      if (onClose) onClose();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          maxWidth: 520,
          borderRadius: 2,
        },
      }}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        if (onClose) onClose();
      }}
    >
      <DialogTitle sx={{ bgcolor: "#9D2235", color: "white", py: 1.5, px: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Test Submitted
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3, textAlign: "center" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            py: 2,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 56, color: "#4caf50" }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#333" }}>
            Test Submitted Successfully!
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#666", maxWidth: 400, lineHeight: 1.6 }}
          >
            Your submission has been received. You can view the detailed result
            for this attempt or go back to the tests list.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, justifyContent: "flex-end", gap: 1 }}>
        <Button
          onClick={handleContinue}
          variant="outlined"
          sx={{
            borderColor: "#9D2235",
            color: "#9D2235",
            "&:hover": {
              borderColor: "#7a182a",
              bgcolor: "rgba(157, 34, 53, 0.04)",
            },
            px: 2,
          }}
        >
          Back To Tests
        </Button>
        <Button
          onClick={handleViewResult}
          variant="contained"
          sx={{
            bgcolor: "#9D2235",
            "&:hover": { bgcolor: "#7a182a" },
            px: 3,
          }}
        >
          View Result
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestResultsPopup;
